import React, { useState } from "react";
import { Table } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import { usePagination, useTable } from "react-table";
import NormalPagination from "../../components/pagination/NormalPagination";
import ArrowIcon from "../../images/arrow_right.svg";
import { ReactComponent as ModalClose } from "../../images/modal_close.svg";
import CIImageAnswerViewer from "../../questions/CI/CIImageAnswerViewer";
import {
    ASCII_CODE_A,
    BUTTON,
    CLOSE_MODAL,
    DEFAULT_ROWS_PER_PAGE,
    ENTRY_TEXT,
    FITB_BLANK_LIMIT,
    FIXED_ANSWER, GRADE_BOOK_RESPONSE_STATUS, 
    POLL_TYPE,
    QUESTION_TYPES,
    SA_ANSWER_LENGTH,
    TEXTBOX,
    UNANSWERED,
    VIEW_TEXT,
    WC_DISPLAY_LIMIT,
} from "../../utils/constants";
import {
    fixFITB, getQuestionParticipationPoints, hasCorrectness,
    isManualGradeQue,
    questionHasCorrectness,
} from "../../utils/helpers";
import styles from "./ViewTakenPollsPage.module.css";
import {LatexInline} from "../../components/Tiptap/latexParser";
import TableSortHeaderNew from "../../components/TableSortHeaderNew";
import {Button} from "@base-ui/react/button";

export default React.memo(TakenPollTable);
function TakenPollTable({
    institutionId="",
    selectedPoll = {},
    setSelectedPoll,
    columns = [],
    setShowAnswerModal,
    setModalData,
    selectedPollLoading=false,
}) {
    const [modalShow, setModalShow] = useState(false);
    const [question, setQuestion] = useState({});
    const [sortedBy, setSortedBy] = useState("");

    const skeletonLoaderWidth = ["50px", "40px", "60px", "30px", "50px"];
    const { questions=[], pollType} = selectedPoll;
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        state: { pageIndex },
    } = useTable({
        columns,
        data: questions,
        initialState: { pageIndex: 0, pageSize: DEFAULT_ROWS_PER_PAGE },
        autoResetPage: true,
    }, usePagination);

    const showQuestion = async (serialNo) => {
        const no = parseInt(serialNo);
        const selectedQuestion = questions.find(q => q.serialNo === no);
        setQuestion(selectedQuestion);
        setModalShow(true);
    };



    const setQuestions = (questions) => {
        const copy = JSON.parse(JSON.stringify(selectedPoll));
        copy.questions = questions;
        setSelectedPoll(copy);
    }

    const statusPresentation = (question) => {
        let display;
        const serialNo = question.serialNo;
        const currQues = questions.find(q => q.serialNo === serialNo);
        const {isCorrect, isGraded, questionType, isPosted=true} = currQues;
        const hasQuestionCorrectness  = questionHasCorrectness(currQues);
        let participationPoints = getQuestionParticipationPoints(currQues);
        if (!isPosted) { // not shared yet
            display = GRADE_BOOK_RESPONSE_STATUS.NOT_POSTED;
        } else if (!hasQuestionCorrectness) {
            display = GRADE_BOOK_RESPONSE_STATUS.UNDEFINED; // does not have weightage
        } else if (isManualGradeQue(hasQuestionCorrectness, questionType)) {
            if (isCorrect) { // isCorrect and isGraded have default value as false
                display = GRADE_BOOK_RESPONSE_STATUS.CORRECT;
            } else if (isGraded) {
                if (participationPoints > 0) {
                    display = GRADE_BOOK_RESPONSE_STATUS.PARTIALLY_CORRECT;
                } else {
                    display = GRADE_BOOK_RESPONSE_STATUS.INCORRECT;
                }

            } else if ((!question.hasOwnProperty("selectedOption") || question.selectedOption === FIXED_ANSWER.UNANSWERED)) {
                display = GRADE_BOOK_RESPONSE_STATUS.UNANSWERED;
            } else {
                display = GRADE_BOOK_RESPONSE_STATUS.UN_GRADED;
            }
        } else {
            if (isCorrect) {
                display = GRADE_BOOK_RESPONSE_STATUS.CORRECT;
            } else if ((!question.hasOwnProperty("selectedOption") || question.selectedOption === FIXED_ANSWER.UNANSWERED)) {
                display = GRADE_BOOK_RESPONSE_STATUS.UNANSWERED;
            } else {
                if (participationPoints > 0) {
                    display = GRADE_BOOK_RESPONSE_STATUS.PARTIALLY_CORRECT;
                } else {
                    display = GRADE_BOOK_RESPONSE_STATUS.INCORRECT;
                }
            }
        }

        return display;
    }

    return (
    <>
        <Modal
            id="question-modal"
            show={modalShow}
            onHide={() => setModalShow(false)}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={true}
            dialogClassName="question-modal2"
        >
            <ModalClose tabIndex={0} role={BUTTON} aria-label={CLOSE_MODAL} onClick={() => setModalShow(false)} className="modal-close" />
            <Modal.Header style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Modal.Title tabIndex={0} role={TEXTBOX}>
                    {`Q${question.serialNo} --- Your Answer`}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CIImageAnswerViewer
                    question={question}
                />
            </Modal.Body>
        </Modal>
        <div className={styles.tableWrapper}>
            <Table hover {...getTableProps()}>
                <thead>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => {
                                if (column.Header === 'Status' && (!hasCorrectness(questions) || pollType?.toUpperCase() === POLL_TYPE.SURVEY)) {
                                    return <></>;
                                }

                                if (column.Header === "Number" || column.Header === "Type") {
                                    return (
                                        <TableSortHeaderNew
                                            data={questions}
                                            setData={setQuestions}
                                            sortedBy={sortedBy}
                                            setSortedBy={setSortedBy}
                                            propertyName={column.id}
                                            label={column.render('Header')}
                                            {...column.getHeaderProps({
                                                style: {
                                                    width: column.width,
                                                    minWidth: column.minWidth
                                                }
                                            })}
                                        >
                                            {column.render('Header')}
                                        </TableSortHeaderNew>
                                    );
                                } else if (column.Header === "Status") {
                                    return (
                                        <TableSortHeaderNew
                                            data={questions}
                                            setData={setQuestions}
                                            sortedBy={sortedBy}
                                            setSortedBy={setSortedBy}
                                            propertyName={column.id}
                                            getProperty={(question) => {
                                                const status = statusPresentation(question);
                                                // getResponseStatus()
                                                return status.order.toString();
                                            }}
                                            label={column.render('Header')}
                                            {...column.getHeaderProps({
                                                style: {
                                                    width: column.width,
                                                    minWidth: column.minWidth
                                                }
                                            })}
                                        >
                                            {column.render('Header')}
                                        </TableSortHeaderNew>
                                    );
                                }

                                return (
                                    <th
                                        {...column.getHeaderProps({
                                            style: {
                                                width: column.width,
                                                minWidth: column.minWidth
                                            }
                                        })}
                                    >
                                        <div className={column.Header === 'Number' ? styles.leftCell : styles.tableCell}>
                                            {column.render('Header')}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {!selectedPollLoading && page.map((row, i) => {
                        prepareRow(row)
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map(cell => {
                                    if (cell.column.Header === 'Number') {
                                        return (
                                            <td {...cell.getCellProps()}>
                                                <div className={styles.leftCell}>
                                                    Q{cell.row.original.serialNo}
                                                </div>
                                            </td>
                                        );
                                    }

                                    if (cell.column.Header === 'Status') {
                                        if (!hasCorrectness(questions) || pollType?.toUpperCase() === POLL_TYPE.SURVEY) return <></>;
                                        let status = statusPresentation(cell.row.original);
                                        return (
                                            <td {...cell.getCellProps()}>
                                                {status === GRADE_BOOK_RESPONSE_STATUS.UNDEFINED ? "---" : status.description}
                                            </td>
                                        );
                                    }

                                    if (cell.column.Header === 'Question') {
                                        if (cell.row.original.questionType === QUESTION_TYPES.FITB.name) {
                                            let display = fixFITB(cell.value);
                                            return (
                                                <td  {...cell.getCellProps()} style={{wordBreak: 'break-word'}}>
                                                    {display}
                                                </td>
                                            );
                                        } else {
                                            return (
                                                <td {...cell.getCellProps()} style={{wordBreak: 'break-word'}}>
                                                    <LatexInline>{cell.value}</LatexInline>
                                                </td>
                                            );
                                        }
                                    }

                                    if (cell.column.Header === 'Type') {
                                        for (let type in QUESTION_TYPES) {
                                            if (QUESTION_TYPES[type].name === cell.value) {
                                                return (
                                                    <td {...cell.getCellProps()}>
                                                        {QUESTION_TYPES[type].description}
                                                    </td>
                                                );
                                            }
                                        };
                                    }

                                    if (cell.column.Header === 'Your Answer') {
                                        const hasCorrectnessPoints = questionHasCorrectness(cell.row.original) && pollType.toUpperCase() !== POLL_TYPE.SURVEY;
                                        if (cell.row.original.hasOwnProperty("isPosted") && !cell.row.original.isPosted) {
                                            return (
                                                <td {...cell.getCellProps()}>
                                                    <div className={styles.leftCell}>
                                                        ---
                                                    </div>
                                                </td>
                                            );
                                        }
                                        if (!cell.row.original.hasOwnProperty("selectedOption") || cell.row.original.selectedOption === FIXED_ANSWER.UNANSWERED) {
                                            return (
                                                <td {...cell.getCellProps()}>
                                                    <div className={styles.leftCell}>
                                                        {!hasCorrectnessPoints ? 'Unanswered' : '---'}
                                                    </div>
                                                </td>
                                            );
                                        }
                                        let display = "---";
                                        var array;
                                        switch (cell.row.original.questionType) {
                                            case QUESTION_TYPES.MCSS.name:
                                                if (Array.isArray(cell.row.original.selectedOption)) {
                                                    if(cell.row.original.selectedOption[0] === FIXED_ANSWER.UNANSWERED) {
                                                        display = UNANSWERED;
                                                    } else {
                                                        display = cell.row.original.selectedOption.sort((a, b) => a.localeCompare(b)).map(idx => idx.toUpperCase()).join(", ");
                                                    }
                                                } else {
                                                    display = cell.row.original.selectedOption.toUpperCase();
                                                }
                                                break;
                                            case QUESTION_TYPES.TF.name:
                                                display = cell.row.original.selectedOption.charAt(0).toUpperCase() + cell.row.original.selectedOption.slice(1);
                                                break;
                                            case QUESTION_TYPES.SA.name:
                                            case QUESTION_TYPES.IB.name:
                                            case QUESTION_TYPES.RETRO.name:
                                            case QUESTION_TYPES.BS.name:
                                                display = cell.row.original.selectedOption["0"];
                                                if (display.length > SA_ANSWER_LENGTH) {
                                                    return (
                                                        <td {...cell.getCellProps()}>
                                                            {/* {display.slice(0, SA_ANSWER_LENGTH)} */}
                                                            <div
                                                                tabIndex={0}
                                                                onClick={() => {
                                                                    setModalData(display);
                                                                    setShowAnswerModal(true);
                                                                }}
                                                                className={styles.clickableAnswer}
                                                                style={{display: "inline-block", margin: "auto", textDecoration: "underline", textAlign: "left"}}
                                                            >
                                                                {VIEW_TEXT}
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                                break;
                                                case QUESTION_TYPES.OE.name:
                                                    display = cell.row.original.selectedOption["0"];
                                                    if (display.length > SA_ANSWER_LENGTH) {
                                                        return (
                                                            <td {...cell.getCellProps()}>
                                                                {/* {display.slice(0, SA_ANSWER_LENGTH)} */}
                                                                <div
                                                                    tabIndex={0}
                                                                    onClick={() => {
                                                                        setModalData(display);
                                                                        setShowAnswerModal(true);
                                                                    }}
                                                                    className={styles.clickableAnswer}
                                                                    style={{display: "inline-block", margin: "auto", textDecoration: "underline", textAlign: "center"}}
                                                                >
                                                                    {VIEW_TEXT}
                                                                </div>
                                                            </td>
                                                        );
                                                    }
                                                    break;
                                            case QUESTION_TYPES.FITB.name:
                                                array = Object.values(cell.row.original.selectedOption);
                                                if (array.length > FITB_BLANK_LIMIT) {
                                                    return (
                                                        <td {...cell.getCellProps()} className={styles.leftCell}>
                                                            {/* {array.slice(0, FITB_BLANK_LIMIT).map((key, index) => { return (<p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>Blank {index + 1}: {key}</p>); })} */}
                                                            <div
                                                                tabIndex={0}
                                                                onClick={() => {
                                                                    setModalData(array.map((key, index) => {return (<p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>Blank {index + 1}: {key}</p>)}));
                                                                    setShowAnswerModal(true);
                                                                }}
                                                                className={styles.clickableAnswer}
                                                                style={{display: "inline-block", margin: "auto", textDecoration: "underline", textAlign: "center"}}
                                                            >
                                                                {VIEW_TEXT}
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td >
                                                        <div className={styles.leftCell}>
                                                            {array.map((key, index) => { return (<p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>Blank {index + 1}: {key}</p>); })}
                                                        </div>
                                                    </td>
                                                );
                                            case QUESTION_TYPES.CI.name:
                                                display = cell.row.original.selectedOption.selected;
                                                return (
                                                    <td className={styles.clickableAnswer} {...cell.getCellProps()}>
                                                        <Button className={styles.leftCell} style={{display: "inline-block"}} onClick={() => showQuestion(cell.row.original.serialNo)}>
                                                            {isNaN(display) ? "View": String.fromCharCode(ASCII_CODE_A + parseInt(display))}
                                                        </Button>
                                                    </td>
                                                );
                                            case QUESTION_TYPES.RK.name: 
                                            array = cell.row.original.selectedOption;
                                                return (
                                                    <td {...cell.getCellProps()}>
                                                        <div
                                                            tabIndex={0}
                                                            onClick={() => {
                                                                setModalData(array.map((key, index) => {
                                                                    return (
                                                                        <p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>
                                                                            Rank {index + 1}: <LatexInline>{key}</LatexInline>
                                                                        </p>
                                                                    )
                                                                }));
                                                                setShowAnswerModal(true);
                                                            }}
                                                            className={styles.clickableAnswer}
                                                            style={{display: "inline-block", margin: "auto", textDecoration: "underline", textAlign: "left"}}
                                                        >
                                                            {VIEW_TEXT}
                                                        </div>
                                                    </td>
                                                );
                                            case QUESTION_TYPES.WC.name:
                                                array = Object.values(cell.row.original.selectedOption).filter(key => key !== "");
                                                if (array.length > WC_DISPLAY_LIMIT) { // display only first 3 words; or limit by characters?
                                                    return (
                                                        <td {...cell.getCellProps()} className={styles.leftCell}>
                                                            {/* {array.slice(0, FITB_BLANK_LIMIT).map((key, index) => { return (<p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>Blank {index + 1}: {key}</p>); })} */}
                                                            <div
                                                                tabIndex={0}
                                                                onClick={() => {
                                                                    setModalData(array.map((key, index) => {
                                                                        if (key === "") return <></>;
                                                                        return (<p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>
                                                                            {ENTRY_TEXT} {index + 1}: {key}
                                                                        </p>)
                                                                    }));
                                                                    setShowAnswerModal(true);
                                                                }}
                                                                className={styles.clickableAnswer}
                                                                style={{display: "inline-block", margin: "auto", textDecoration: "underline", textAlign: "left"}}
                                                            >

                                                                {VIEW_TEXT}
                                                            </div>
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td {...cell.getCellProps()}>
                                                        <div className={styles.leftCell}>
                                                            {array.map((key, index) => {
                                                                return (<p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>
                                                                    {key}
                                                                </p>)    
                                                            })}
                                                        </div>
                                                    </td>
                                                );
                                            case QUESTION_TYPES.MH.name: 
                                                array = Object.keys(cell.row.original.selectedOption); 
                                                let values = cell.row.original.selectedOption; 
                                                let options = cell.row.original.optionsMap.filter(sublist => sublist.length === 2);
                                                return (
                                                    <td {...cell.getCellProps()} className={styles.leftCell}>
                                                        {/* {array.slice(0, FITB_BLANK_LIMIT).map((key, index) => { return (<p key={index} style={{ marginTop: 6, marginBottom: 6, wordBreak: 'break-word' }}>Blank {index + 1}: {key}</p>); })} */}
                                                        <div
                                                            tabIndex={0}
                                                            className={styles.clickableAnswer}
                                                            onClick={() => {
                                                                setModalData(options.map((key, index) => {
                                                                    if (key === "") return <></>;
                                                                    return (
                                                                        <div
                                                                            style={{ display: "flex", width: "100%", gap: "10px", alignItems: "center", justifyContent: "center"}}
                                                                        >
                                                                            <p
                                                                                className="questionPanelAnswersText"
                                                                                id={"OptionPart1" + index}
                                                                                style={{ marginRight: "0px", border: ".5px solid #909090", padding: "10px", borderRadius: "3px", minWidth: "200px", flex: 1, marginBottom: "0px", marginTop: "10px"}}
                                                                            >
                                                                                <LatexInline>{key[0]}</LatexInline>
                                                                            </p>
                                                                            <img
                                                                                src={ArrowIcon}
                                                                                alt="Arrow icon"
                                                                                className="questions-page-icons cursor-pointer"
                                                                                style={{ width: "30px", margin: "10px 0px 0px 10px" }}
                                                                            />
                                                                            <p
                                                                                className="questionPanelAnswersText"
                                                                                id={"OptionPart2" + index}
                                                                                style={{ border: ".5px solid #909090", padding: "10px", borderRadius: "3px", minWidth: "200px", flex: 1,  marginBottom: "0px", marginTop: "10px" }}
                                                                            >
                                                                                {values.hasOwnProperty(index) && !!values[index]
                                                                                    ? <LatexInline>{values[index]}</LatexInline>
                                                                                    : UNANSWERED
                                                                                }
                                                                            </p>
                                                                        </div>
                                                                    )
                                                                }));
                                                                setShowAnswerModal(true);
                                                            }}
                                                            style={{display: "inline-block", margin: "auto", textDecoration: "underline", textAlign: "left"}}
                                                        >
                                                            {VIEW_TEXT}
                                                        </div>
                                                    </td>
                                                );

                                            default:
                                                break;
                                        }
                                        if (cell.row.original.questionType !== QUESTION_TYPES.FITB.name) {
                                            return (
                                                <td style={{wordBreak: 'break-word'}} {...cell.getCellProps()}>
                                                    <div className={styles.leftCell}>
                                                        {display}
                                                    </div>
                                                </td>
                                            );
                                        }
                                    }

                                    return (
                                        <td {...cell.getCellProps()}>
                                            <div className={styles.leftCell}>
                                                {cell.value}
                                            </div>
                                        </td>
                                    );

                                })}
                            </tr>
                        )
                    })}

                    {
                        selectedPollLoading &&  (Array.from({ length: 5 }).map((_, rowIndex) => (
                            <tr key={rowIndex}> {headerGroups.map(headerGroup => (
                                <>
                                {headerGroup.headers.map(column => {
                                    if (column.Header === 'Status' && (!hasCorrectness(questions) || pollType?.toUpperCase() === POLL_TYPE.SURVEY)) return <></>;
                                    return (
                                    <td  {...column.getHeaderProps({
                                        style: {
                                            width: column.width,
                                            minWidth: column.minWidth,
                                            height: 40
                                        }
                                    })}>
                                        <div className={column.Header === 'Number' ? styles.leftCell : styles.tableCell}>
                                            <SkeletonTheme color="rgba(237, 237, 237, 1)" highlightColor="lightgrey" >
                                                <Skeleton borderRadius={10} height={14} width={skeletonLoaderWidth[rowIndex]} style={{ position: "relative" }} />
                                            </SkeletonTheme>
                                        </div>
                                    </td>);
                                })}
                                </> 
                                ))}
                            </tr>
                        )))
                    }
                </tbody>
            </Table>
        </div>

        {page && page.length !== 0 &&
            <div style={{  bottom: "25px" }}>
                <NormalPagination
                    pageCount={pageCount}
                    pageIndex={pageIndex}
                    pageOptions={pageOptions}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    previousPage={previousPage}
                    nextPage={nextPage}
                    gotoPage={gotoPage}
                />
            </div>
        }
    </>
    );
}
